import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ghost from '../../api/ghost'; // Import the new ghost client
import { BlogListSkeleton, StaticBlogListSkeleton } from './SkeletonComponents';

// Simplified image component with direct loading
const BlogImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  // Handle image load event
  const handleLoad = () => {
    setLoaded(true);
  };

  // Handle image error event
  const handleError = () => {
    setError(true);
    setLoaded(true); // Consider it "loaded" even on error to remove placeholder
  };

  return (
    <div className={`relative overflow-hidden ${className || ''}`}>
      {/* Show placeholder until loaded */}
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-800 to-neutral-900 animate-pulse" />
      )}
      
      {/* The actual image */}
      <img
        src={src || ''}
        alt={alt || ''}
        className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={handleLoad}
        onError={handleError}
        loading="eager" // Load all images eagerly for now to guarantee they appear
      />
      
      {/* Show error message if image failed to load */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <span className="text-white text-xs px-2 py-1 bg-black/70 rounded">Image unavailable</span>
        </div>
      )}
    </div>
  );
};

// Simplified PostItem component
const PostItem = ({ post }) => {
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get reading time helper
  const getReadingTime = (post) => {
    return post?.reading_time || 5;
  };

  return (
    <Link to={`/blog/${post.slug}`} className="block h-full">
      <div className="bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden hover:border-[#00CF9D] transition-all duration-300 h-full flex flex-col">
        {/* Image section with fixed height */}
        <div className="relative h-48 bg-neutral-800">
          {post.feature_image ? (
            <BlogImage 
              src={post.feature_image} 
              alt={post.title}
              className="w-full h-full" 
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-neutral-900">
              <span className="text-neutral-500 text-sm">No image</span>
            </div>
          )}
        </div>
        
        {/* Content section */}
        <div className="p-4 flex flex-col flex-grow">
          <h3 className="text-lg font-bold mb-2 line-clamp-2 text-white">
            {post.title}
          </h3>
          
          <p className="text-sm text-neutral-400 mb-3 line-clamp-2">
            {post.excerpt || post.custom_excerpt || ''}
          </p>
          
          <div className="mt-auto pt-3 flex text-xs text-neutral-500 justify-between items-center">
            <span>{formatDate(post.published_at)}</span>
            <span>{getReadingTime(post)} min read</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Featured post component
const FeaturedPost = React.memo(({ post, formatDate, getReadingTime }) => (
  <Link to={`/blog/${post.slug}`} className="block">
    <div className="bg-neutral-900 rounded-2xl overflow-hidden flex flex-col md:flex-row border border-neutral-800 hover:border-[#00CF9D] transition-colors duration-300">
      <div className="md:w-1/2 p-6 md:p-8 flex flex-col">
        <div className="mb-2">
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {post.tags.slice(0, 1).map(tag => (
                <span key={tag.id} className="text-xs bg-neutral-800 text-neutral-300 px-2 py-1 rounded-full">
                  {tag.name}
                </span>
              ))}
            </div>
          )}
          <h2 className="text-2xl md:text-3xl font-mono font-bold mb-3 text-[#DBDBDB]">{post.title}</h2>
          <p className="text-neutral-300 mb-4 text-sm md:text-base line-clamp-3">{post.excerpt}</p>
        </div>
        <div className="mt-auto">
          {post.primary_author && (
            <div className="flex items-center mb-4">
              <div className="text-sm flex items-center">
                {post.published_at && (
                  <time className="mr-2 text-neutral-400">{formatDate(post.published_at)}</time>
                )}
                <span 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    window.location.href = `/blog/author/${post.primary_author.slug}`;
                  }}
                  className="text-[#00CF9D] hover:underline group cursor-pointer"
                >
                  by {post.primary_author.name}
                </span>
              </div>
            </div>
          )}
          <div className="flex items-center text-sm text-neutral-400 mb-4">
            {post.reading_time && (
              <span>{getReadingTime(post.reading_time)}</span>
            )}
          </div>
          <div 
            className="inline-flex h-9 items-center justify-center rounded-md bg-[#00CF9D]/5 border border-[#00CF9D]/10 px-4 text-sm font-medium text-[#00CF9D] transition-colors hover:bg-[#00CF9D]/10 focus:outline-none focus:ring-2 focus:ring-[#00CF9D] focus:ring-offset-2 focus:ring-offset-neutral-900"
          >
            Read More →
          </div>
        </div>
      </div>
      <div className="md:w-1/2 relative">
        {post.feature_image ? (
          <img 
            src={post.feature_image} 
            alt={post.feature_image_alt || post.title}
            className="w-full h-64 md:h-full object-cover"
            loading="eager" // Eagerly load the first image
          />
        ) : (
          <div className="w-full h-64 md:h-full bg-neutral-800 flex items-center justify-center">
            <span className="text-neutral-400">No image available</span>
          </div>
        )}
        <div className="absolute bottom-4 right-4 bg-black/70 px-2 py-1 rounded text-xs text-white">
          {getReadingTime(post.reading_time || 5)}
        </div>
      </div>
    </div>
  </Link>
));

function BlogList({ tag = null }) {
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const initialLoadRef = useRef(true);
  const loadMoreRef = useRef(null);
  const loadMoreObserver = useRef(null);
  const [visibleItemCount, setVisibleItemCount] = useState(30);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const navigate = useNavigate();
  
  // Helper functions
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, []);

  const getReadingTime = useCallback((minutes) => {
    return `${minutes} min read`;
  }, []);

  // Adjusted intersection observer logic for better load detection
  useEffect(() => {
    if (!loadMoreObserver.current && !isLoading && posts.length > 0 && isInitialized) {
      loadMoreObserver.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && posts.length > visibleItemCount) {
            // Load more posts when the user scrolls to the bottom
            setVisibleItemCount(prev => Math.min(prev + 6, posts.length));
          }
        },
        { rootMargin: '300px', threshold: 0.1 } // More aggressive loading
      );
      
      if (loadMoreRef.current) {
        loadMoreObserver.current.observe(loadMoreRef.current);
      }
    }
    
    return () => {
      if (loadMoreObserver.current) {
        loadMoreObserver.current.disconnect();
      }
    };
  }, [isLoading, posts.length, visibleItemCount, isInitialized]);

  useEffect(() => {
    // Keep showing skeleton for initial load only
    const showSkeletonLoader = initialLoadRef.current;
    
    if (!showSkeletonLoader) {
      setIsLoading(true);
    }
    
    setError(null);
    
    let filterString = 'visibility:public';
    
    // Add tag filter if provided
    if (tag) {
      filterString += `+tag:${tag}`;
    }
    
    // Load data with Ghost API
    ghost.posts.browse({
      page,
      limit: 20,
      include: ['authors', 'tags'],
      filter: filterString,
      order: 'published_at DESC'
    })
    .then((response) => {
      setPosts(response);
      
      // Handle pagination from meta data
      if (response.meta && response.meta.pagination) {
        setPagination(response.meta.pagination);
      } else {
        // Fallback pagination if meta is not available
        setPagination({
          page: 1,
          limit: 10,
          pages: 1,
          total: response.length,
          next: null,
          prev: null
        });
      }
      
      setIsLoading(false);
      initialLoadRef.current = false;
      
      // Show all posts initially, but smaller number of posts for subsequent pages
      if (page === 1) {
        setVisibleItemCount(response.length > 15 ? response.length : 15);
      } else {
        setVisibleItemCount(prev => prev + 9);
      }
      
      setIsInitialized(true);
    })
    .catch((err) => {
      console.error('Ghost API Error:', err);
      setError('Failed to load blog posts. Please try again later.');
      setIsLoading(false);
      initialLoadRef.current = false;
      setIsInitialized(true);
    });
  }, [page, tag]);

  // Preload next page data when on current page to make pagination feel instant
  useEffect(() => {
    if (pagination && pagination.next) {
      const preloadNextPage = async () => {
        let filterString = 'visibility:public';
        if (tag) filterString += `+tag:${tag}`;
        
        // Replace this with actual API call once you set up your Ghost API
      };
      
      preloadNextPage();
    }
  }, [pagination, page, tag]);

  // Apply loading overlay for pagination changes
  const renderContent = useCallback(() => {
    // Add a safety check to ensure posts is an array
    if (!posts || !Array.isArray(posts) || posts.length === 0) {
      return (
        <div className="text-neutral-300 py-8">
          <p className="mb-4">No blog posts found {tag ? `with tag "${tag}"` : ''}.</p>
          <p>Check back soon for new content!</p>
        </div>
      );
    }
    
    const featuredPost = posts[0];
    const remainingPosts = posts.slice(1);
    
    // When we have very few posts, show all of them without pagination
    const displayPosts = remainingPosts.length <= 9 
      ? remainingPosts 
      : remainingPosts.slice(0, visibleItemCount);
    
    const showLoadMore = remainingPosts.length > 9 && visibleItemCount < remainingPosts.length;
    
    return (
      <div className="space-y-12 relative">
        {/* Add loading overlay for pagination changes */}
        {isLoading && !initialLoadRef.current && (
          <div className="absolute inset-0 bg-black/50 z-10 flex items-center justify-center rounded-xl">
            <div className="loader text-[#00CF9D] flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading posts...</span>
            </div>
          </div>
        )}
        
        {/* Featured post */}
        {featuredPost && (
          <FeaturedPost 
            post={featuredPost} 
            formatDate={formatDate} 
            getReadingTime={getReadingTime} 
          />
        )}

        {/* Grid of remaining posts */}
        {remainingPosts.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {displayPosts.map((post) => (
                <PostItem
                  key={post.id}
                  post={post}
                />
              ))}
            </div>
            
            {/* Intersection observer target for infinite scroll */}
            {showLoadMore && (
              <div ref={loadMoreRef} className="py-4 flex justify-center">
                <div className="loader text-[#00CF9D] flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm">Loading more posts...</span>
                </div>
              </div>
            )}
          </>
        )}
        
        {pagination && pagination.pages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-12">
            <button 
              disabled={!pagination.prev} 
              onClick={() => setPage(page - 1)}
              className={`px-4 py-2 rounded-md border font-mono text-sm ${
                !pagination.prev 
                  ? 'border-neutral-700 bg-neutral-900 text-neutral-500 cursor-not-allowed' 
                  : 'border-[#00CF9D]/20 bg-[#00CF9D]/5 text-[#00CF9D] hover:bg-[#00CF9D]/10'
              }`}
            >
              Previous
            </button>
            <span className="text-neutral-400 font-mono text-sm">Page {pagination.page} of {pagination.pages}</span>
            <button 
              disabled={!pagination.next} 
              onClick={() => setPage(page + 1)}
              className={`px-4 py-2 rounded-md border font-mono text-sm ${
                !pagination.next 
                  ? 'border-neutral-700 bg-neutral-900 text-neutral-500 cursor-not-allowed' 
                  : 'border-[#00CF9D]/20 bg-[#00CF9D]/5 text-[#00CF9D] hover:bg-[#00CF9D]/10'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  }, [posts, isLoading, initialLoadRef, pagination, page, visibleItemCount, formatDate, getReadingTime, loadMoreRef, tag]);

  if (isLoading && initialLoadRef.current) return (
    <div className="flex justify-center items-center py-16">
      <div className="loader text-[#00CF9D] flex items-center">
        <svg className="animate-spin mr-3 h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Loading content...</span>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="relative">
      <div className="mb-4 text-[#00CF9D] p-4 bg-neutral-900 border border-[#00CF9D]/20 rounded-md">
        <h3 className="font-mono font-bold mb-2">Oops! We couldn't load the content</h3>
        <p className="mb-2 text-neutral-300">{error}</p>
      </div>
    </div>
  );
  
  // Make sure posts is an array before checking its length
  if (!posts || !Array.isArray(posts) || posts.length === 0) {
    return (
      <div className="text-neutral-300 py-8">
        <p className="mb-4">No blog posts found {tag ? `with tag "${tag}"` : ''}.</p>
        <p>Check back soon for new content!</p>
      </div>
    );
  }

  return renderContent();
}

export default BlogList;